import React, { useState, useRef, useEffect } from "react";
import moment from "moment";
import "@fullcalendar/daygrid/main.css";
import { CircularProgress } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useSnackbar } from "notistack";
import { useParams, useSearchParams } from "react-router-dom";
import GeneralCalendar from "src/components/GeneralComponents/GeneralCalendar";
import { programme_workout_list_api } from "src/DAL/programmeWorkout/programmeWorkout";
import { programme_detail_api } from "src/DAL/Programme/Programme";
import { show_proper_words } from "src/utils/constant";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const useStyles = makeStyles(() => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));

const get_view_dates = (calendarValue, referenceDate) => {
  let tool = calendarValue;
  const base = referenceDate ? moment(referenceDate) : moment();
  const startOfMonth = base.clone().startOf(tool);
  const daysToSubtract = startOfMonth.day() - 0;
  const previousSunday = startOfMonth.subtract(daysToSubtract, "days");
  let previousSundayDate = previousSunday.toDate();

  let add_days = 42;
  if (tool == "week") {
    add_days = 6;
  } else if (tool == "day") {
    add_days = 0;
  }

  let nextSaturday = startOfMonth.add(add_days, "days");
  let nextSaturdayDate = nextSaturday.toDate();
  if (tool == "day") {
    previousSundayDate = referenceDate ? new Date(referenceDate) : new Date();
    nextSaturdayDate = referenceDate ? new Date(referenceDate) : new Date();
  }

  return {
    start_date: previousSundayDate,
    end_date: nextSaturdayDate,
  };
};

function Calenders() {
  const classes = useStyles();
  const { id, slug } = useParams();
  const [searchParams] = useSearchParams();
  const calendarRef = useRef();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [isPasted, setIsPasted] = useState(false);
  const [defaultTimeZone, setDefaultTimeZone] = useState("");

  // If URL has a valid date param (e.g. shared link), initialize calendar to that month
  const urlDate = searchParams.get("date");
  const isValidUrlDate =
    urlDate && moment(urlDate, "YYYY-MM-DD", true).isValid();
  const [currentDate, setCurrentDate] = useState(
    get_view_dates("month", isValidUrlDate ? urlDate : undefined),
  );
  const [difficultyLevel, setDifficultyLevel] = useState([]);
  const [manualLoading, setManualLoading] = useState(false);
  const isInitialLoad = useRef(true);
  const [programmeData, setProgrammeData] = useState(null);
  const reloadRequestRef = useRef({
    id: null,
    message: null,
    skipScroll: false,
    scrollY: 0,
    calScrolls: null,
    isLevelChange: false,
  });

  useEffect(() => {
    const fetchProgrammeDetails = async () => {
      const programmeId = id;
      if (programmeId) {
        const result = await programme_detail_api(programmeId);
        if (result.code === 200) {
          setProgrammeData(result.program);
        } else {
          enqueueSnackbar(result.message, { variant: "error" });
        }
      }
    };
    fetchProgrammeDetails();
  }, [id, slug, enqueueSnackbar]);

  const goToToday = () => {
    if (calendarRef?.current) {
      const calendarApi = calendarRef.current.getApi();
      const today = new Date();
      const viewStart = calendarApi.view.currentStart;
      const viewMonth = viewStart.getMonth();
      const viewYear = viewStart.getFullYear();

      const todayMonth = today.getMonth();
      const todayYear = today.getFullYear();

      const isSameMonth = viewMonth === todayMonth && viewYear === todayYear;

      if (!isSameMonth) {
        calendarApi.gotoDate(today);
        const newDateRange = {
          start_date: calendarApi.view.activeStart,
          end_date: calendarApi.view.activeEnd,
        };
        setCurrentDate(newDateRange);
      } else {
        const todayDate = today.toISOString().slice(0, 10);
        const todayCell = document.querySelector(
          `.fc-day[data-date="${todayDate}"]`,
        );
        if (todayCell) {
          todayCell.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }
  };

  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    reloadRequestRef.current = {
      ...reloadRequestRef.current,
      isLevelChange: true,
    };
  }, [difficultyLevel]);

  let Levels =
    difficultyLevel.length > 0
      ? difficultyLevel.map((level) => level).join(", ")
      : [];

  const prefetchAdjacentMonths = async () => {
    if (!calendarRef?.current) return;
    const calendarApi = calendarRef.current.getApi();
    const originalDate = calendarApi.getDate();

    calendarApi.next();
    const nextView = calendarApi.view;
    const nextRange = {
      start_date: nextView.activeStart,
      end_date: nextView.activeEnd,
    };

    calendarApi.gotoDate(originalDate);

    calendarApi.prev();
    const prevView = calendarApi.view;
    const prevRange = {
      start_date: prevView.activeStart,
      end_date: prevView.activeEnd,
    };

    calendarApi.gotoDate(originalDate);

    const nextMonthPostData = {
      start_date: moment(nextRange.start_date).format("YYYY-MM-DD"),
      end_date: moment(nextRange.end_date).format("YYYY-MM-DD"),
      slug: slug,
      level_data: Levels,
    };

    const prevMonthPostData = {
      start_date: moment(prevRange.start_date).format("YYYY-MM-DD"),
      end_date: moment(prevRange.end_date).format("YYYY-MM-DD"),
      slug: slug,
      level_data: Levels,
    };

    try {
      await Promise.all([
        queryClient.prefetchQuery({
          queryKey: ["programWorkouts", nextRange, slug, difficultyLevel],
          queryFn: async () => {
            const result = await programme_workout_list_api(nextMonthPostData);
            if (result.code === 200) {
              return result?.program_workouts || [];
            }
            return [];
          },
        }),
        queryClient.prefetchQuery({
          queryKey: ["programWorkouts", prevRange, slug, difficultyLevel],
          queryFn: async () => {
            const result = await programme_workout_list_api(prevMonthPostData);
            if (result.code === 200) {
              return result?.program_workouts || [];
            }
            return [];
          },
        }),
      ]);
    } catch (error) {
      console.log("Prefetch error:", error);
    }
  };

  const handleScrollCalled = (data) => {
    const todayCell = document.querySelector(
      `.calendar-workout-card[id="${data}"]`,
    );
    if (todayCell)
      todayCell.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const fetchWorkouts = async () => {
    setManualLoading(true);
    let postData = {
      start_date: moment(currentDate.start_date).format("YYYY-MM-DD"),
      end_date: moment(currentDate.end_date).format("YYYY-MM-DD"),
      slug: slug,
      level_data: Levels,
    };
    const result = await programme_workout_list_api(postData);
    if (result.code === 200) {
      setManualLoading(false);
      return result?.program_workouts || [];
    } else {
      setManualLoading(false);
      throw new Error(result.message);
    }
  };

  const {
    data: workouts = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["programWorkouts", currentDate, slug, difficultyLevel],
    queryFn: fetchWorkouts,
    keepPreviousData: true,
    onSuccess: (data) => {
      prefetchAdjacentMonths();
      setDefaultTimeZone("asia/karachi");
      setIsPasted(false);
      const { id, message, skipScroll, isLevelChange } =
        reloadRequestRef.current;
      if (message) {
        enqueueSnackbar(message, { variant: "success" });
      }

      if (isLevelChange) {
        // level change — don't scroll at all, just stay where user is
      } else if (!skipScroll) {
        if (id) {
          setTimeout(() => handleScrollCalled(id), 100);
        } else {
          // const today = new Date();
          // const todayDate = today.toISOString().slice(0, 10);
          // const todayCell = document.querySelector(
          //   `.fc-day[data-date="${todayDate}"]`,
          // );
          // if (todayCell) {
          //   todayCell.scrollIntoView({ behavior: "smooth", block: "center" });
          // }
        }
      } else {
        const savedScrollY = reloadRequestRef.current.scrollY;
        const savedCalScrolls = reloadRequestRef.current.calScrolls;
        requestAnimationFrame(() => {
          window.scrollTo({ top: savedScrollY, behavior: "instant" });
          if (savedCalScrolls) {
            const scrollers = document.querySelectorAll(".fc-scroller");
            savedCalScrolls.forEach(({ index, top, left }) => {
              const el = scrollers[index];
              if (el) {
                el.scrollTop = top;
                el.scrollLeft = left;
              }
            });
          }
        });
      }
      reloadRequestRef.current = {
        id: null,
        message: null,
        skipScroll: false,
        scrollY: 0,
        calScrolls: null,
        isLevelChange: false,
      };
    },
    onError: (error) => {
      setIsPasted(false);
      setManualLoading(false);
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const handleReload = (
    reloadId,
    message,
    setOpenCopyPasteConfig,
    skipScroll = false,
  ) => {
    if (setOpenCopyPasteConfig) {
      setOpenCopyPasteConfig(false);
    }

    let calScrolls = null;
    if (skipScroll) {
      const scrollers = document.querySelectorAll(".fc-scroller");
      calScrolls = Array.from(scrollers).map((el, i) => ({
        index: i,
        top: el.scrollTop,
        left: el.scrollLeft,
      }));
    }

    reloadRequestRef.current = {
      id: reloadId,
      message: message,
      skipScroll: skipScroll,
      scrollY: skipScroll ? window.scrollY : 0,
      calScrolls,
    };
    refetch();
  };

  if (isLoading) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }

  return (
    <GeneralCalendar
      programWorkoutList={workouts}
      setCurrentDate={setCurrentDate}
      showAddEvent={true}
      time_zone={defaultTimeZone}
      currentDate={currentDate}
      isLoading={isLoading}
      setIsLoading={() => {}}
      reloadList={handleReload}
      calendar_title={programmeData?.title || show_proper_words(slug)}
      programme={programmeData}
      isPasted={isPasted}
      setIsPasted={setIsPasted}
      difficultyLevel={difficultyLevel}
      setDifficultyLevel={setDifficultyLevel}
      calendarRef={calendarRef}
      goToToday={goToToday}
      manualLoading={manualLoading}
      initialDate={isValidUrlDate ? urlDate : undefined}
    />
  );
}

export default Calenders;
